import type { UploadFile } from 'tdesign-react';
import type { UserInfo as IntlUserInfo } from '@ssv-website/hooks';
import {
  getUploadPreviewUrl,
  getUploadSubmitUrl,
  // isAvatarUploadReady — temporarily disabled; restore when avatar is required again
} from './AvatarUploadField';
import { MAX_TITLE_COUNT, TITLE_ROW_COUNT } from './config';
import type {
  ConferenceGuestDetail,
  ConferenceRegisterPayload,
  ConferenceTitleItem,
  SignupFormValues,
} from './types';

type MergeableTextField = keyof Pick<
  SignupFormValues,
  | 'firstName'
  | 'lastName'
  | 'nameCn'
  | 'nationality'
  | 'email'
  | 'organization'
  | 'position'
  | 'bioCn'
  | 'bioEn'
>;

const MERGEABLE_TEXT_FIELDS: MergeableTextField[] = [
  'firstName',
  'lastName',
  'nameCn',
  'nationality',
  'email',
  'organization',
  'position',
  'bioCn',
  'bioEn',
];

const trimText = (value: unknown) => String(value ?? '').trim();
const hasMaskedCharacter = (value: unknown) =>
  String(value ?? '').includes('*');
const normalizeSubmitEmail = (value: unknown) => {
  const email = trimText(value);
  return hasMaskedCharacter(email) ? '' : email;
};

export const createEmptyTitleRow = (): ConferenceTitleItem => ({
  titleCn: '',
  titleEn: '',
});

export const createDefaultFormValues = (): SignupFormValues => ({
  avatar: [],
  firstName: '',
  lastName: '',
  nameCn: '',
  titles: Array.from({ length: TITLE_ROW_COUNT }, () => createEmptyTitleRow()),
  nationality: '',
  email: '',
  organization: '',
  position: '',
  bioCn: '',
  bioEn: '',
  allowContact: 1,
});

export const createTitleRows = (value: unknown): ConferenceTitleItem[] => {
  const normalized = Array.isArray(value)
    ? value
        .map((item) => ({
          titleCn: trimText((item as Record<string, unknown>)?.titleCn),
          titleEn: trimText((item as Record<string, unknown>)?.titleEn),
        }))
        .filter((item) => item.titleCn || item.titleEn)
        .slice(0, MAX_TITLE_COUNT)
    : [];

  while (normalized.length < TITLE_ROW_COUNT) {
    normalized.push(createEmptyTitleRow());
  }

  return normalized;
};

export const getFilledTitleItems = (value: unknown): ConferenceTitleItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => ({
      titleCn: trimText((item as Record<string, unknown>)?.titleCn),
      titleEn: trimText((item as Record<string, unknown>)?.titleEn),
    }))
    .filter((item) => item.titleCn || item.titleEn);
};

export const createInitialAvatarFiles = (avatar: unknown) => {
  const avatarUrl = getUploadPreviewUrl(avatar);

  if (!avatarUrl) {
    return [] as UploadFile[];
  }

  return [
    {
      name: 'avatar.png',
      status: 'success',
      url: avatarUrl,
    } as UploadFile,
  ];
};

export const buildStaticInitialValues = (
  userInfo: IntlUserInfo | null
): Partial<SignupFormValues> => ({
  firstName: trimText(userInfo?.firstName),
  lastName: trimText(userInfo?.lastName),
  email: trimText(userInfo?.email),
  organization: trimText(userInfo?.company),
  position: trimText(userInfo?.position),
});

export const buildGuestDetailInitialValues = (
  detail: ConferenceGuestDetail
): Partial<SignupFormValues> => ({
  avatar: createInitialAvatarFiles(detail.avatar),
  firstName: trimText(detail.firstName),
  lastName: trimText(detail.lastName),
  nameCn: trimText(detail.nameCn),
  titles: createTitleRows(detail.titles),
  nationality: trimText(detail.nationality),
  email: trimText(detail.email),
  organization: trimText(detail.organization),
  position: trimText(detail.position),
  bioCn: trimText(detail.bioCn),
  bioEn: trimText(detail.bioEn),
  allowContact: detail.allowContact === 0 ? 0 : 1,
});

export const mergeInitialValues = (
  ...sources: Array<Partial<SignupFormValues> | undefined>
) => {
  const base = createDefaultFormValues();
  const lastSourceIndex = sources.length - 1;

  sources.forEach((item, index) => {
    if (!item) return;
    const isLastSource = index === lastSourceIndex;

    if (item.avatar?.length) {
      base.avatar = item.avatar;
    }
    if (item.titles?.length) {
      base.titles = createTitleRows(item.titles);
    }

    for (const field of MERGEABLE_TEXT_FIELDS) {
      const value = item[field];
      const hasValue = hasTextValue(value);
      const isExplicitlyCleared = isLastSource && value === '';

      if (
        (hasValue || isExplicitlyCleared) &&
        !(
          field === 'email' &&
          hasMaskedCharacter(value) &&
          hasTextValue(base.email) &&
          !hasMaskedCharacter(base.email)
        )
      ) {
        base[field] = trimText(value);
      }
    }

    if (typeof item.allowContact === 'number') {
      base.allowContact = item.allowContact === 0 ? 0 : 1;
    }
  });

  return base;
};

export const buildConferenceRegisterPayload = (
  values: Partial<SignupFormValues>
): ConferenceRegisterPayload => ({
  avatar: getUploadSubmitUrl(values.avatar),
  firstName: trimText(values.firstName),
  lastName: trimText(values.lastName),
  nameCn: trimText(values.nameCn),
  titles: getFilledTitleItems(values.titles),
  nationality: trimText(values.nationality),
  email: normalizeSubmitEmail(values.email),
  organization: trimText(values.organization),
  position: trimText(values.position),
  bioCn: trimText(values.bioCn),
  bioEn: trimText(values.bioEn),
  allowContact: values.allowContact === 0 ? 0 : 1,
});

const normalizeComparableTitleItems = (value: unknown) =>
  getFilledTitleItems(value).map((item) => ({
    titleCn: trimText(item.titleCn),
    titleEn: trimText(item.titleEn),
  }));

const buildComparableConferenceRegisterPayload = (
  payload: ConferenceRegisterPayload
): ConferenceRegisterPayload => ({
  ...payload,
  avatar: trimText(payload.avatar),
  firstName: trimText(payload.firstName),
  lastName: trimText(payload.lastName),
  nameCn: trimText(payload.nameCn),
  titles: normalizeComparableTitleItems(payload.titles),
  nationality: trimText(payload.nationality),
  email: trimText(payload.email),
  organization: trimText(payload.organization),
  position: trimText(payload.position),
  bioCn: trimText(payload.bioCn),
  bioEn: trimText(payload.bioEn),
  allowContact: payload.allowContact === 0 ? 0 : 1,
});

export const isConferenceRegisterPayloadChanged = (
  current: ConferenceRegisterPayload,
  initial: ConferenceRegisterPayload
) => {
  // Chat profile updates compare the final API payload so upload objects and empty title rows do not create false changes.
  const currentComparable = buildComparableConferenceRegisterPayload(current);
  const initialComparable = buildComparableConferenceRegisterPayload(initial);

  return JSON.stringify(currentComparable) !== JSON.stringify(initialComparable);
};

export const hasTextValue = (value: unknown) => trimText(value).length > 0;

export const isSignupFormSubmittable = (
  values: Partial<SignupFormValues> | undefined
) => {
  if (!values) {
    return false;
  }

  const titles = getFilledTitleItems(values.titles);
  const [firstTitle] = titles;

  // TODO: restore avatar required check when the feature is re-enabled
  // if (!isAvatarUploadReady(values.avatar)) {
  //   return false;
  // }

  if (
    !hasTextValue(values.firstName) ||
    !hasTextValue(values.lastName)
  ) {
    return false;
  }

  if (
    !hasTextValue(firstTitle?.titleCn) ||
    !hasTextValue(firstTitle?.titleEn)
  ) {
    return false;
  }

  if (!hasTextValue(values.bioCn) || !hasTextValue(values.bioEn)) {
    return false;
  }

  return values.allowContact === 0 || values.allowContact === 1;
};
